import { useCallback, useEffect, useMemo, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { notice } from '@/components/pbo/notice'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'

/** 컨덕터 `core.FormItem` 라벨 고정폭 — 산출물이 `labelWidth={88}` 로 내보낸다(DS 요소 기본값은 160 이라 명시 필수). */
const FI_LABEL_WIDTH = 88

/** 상세사유 최소 길이 — 1.0 RemarkPop 검증과 동일. */
export const REASON_DETAIL_MIN_LENGTH = 5

/**
 * 상세사유 최대 길이.
 *
 * BE 는 사유를 감사로그 `MOMS_USER_ACCESS_LOG.remark`(varchar 1000)에 넣고, 엑셀은 GET 이라
 * 사유가 쿼리스트링에 실린다. 한글은 URL 인코딩 시 3배로 불어나므로 넉넉히 잡아도 200 이면 충분하다.
 */
export const REASON_DETAIL_MAX_LENGTH = 200

interface Props {
  open: boolean
  onConfirm: (params: { reasonCode: string; reasonDetail: string }) => void
  onCancel: () => void
}

/**
 * GR05 입고조회(전체) 엑셀 다운로드 사유 모달(1.0 RemarkPop 동치) — 컨덕터 `cmp.DownloadReasonModal` 산출물(#inqAllDownloadModal) 구조 그대로:
 * `core.Modal('다운로드 사유'·520·취소) > core.Form(horizontal) > [FormItem 사유코드(required·88) Radio.Group · FormItem 상세사유(required·88) Input + Typography.Text(12·secondary)]`.
 * 인스턴스 `withPassword:false`(testInboundInquiryAll.ts:137). 검증은 `okValidate` — 확인 상시 활성, 클릭 시 첫 실패를 warning 으로.
 *
 * pbo-core 통짜 `ExcelDownloadReasonModal` 은 쓰지 않는다 — `onConfirm()` 이 무인자라 사유값을 꺼낼 수 없고(BE `detailedReason`
 * 감사로그 계약 불충족), 파일 비밀번호가 필수인데 GR05 는 암호 zip 계약이 없다(옛 통짜 26종 금지 — 2026-09-08 브리핑 §1).
 * 사유는 다운로드 요청 파라미터(`detailedReason`)로 실어 BE 가 EXPORT 감사로그에 남긴다(LOGISPRD-9602).
 */
export function ExcelDownloadReasonModal({ open, onConfirm, onCancel }: Readonly<Props>) {
  const { data: combo, isLoading: isComboLoading, isError: isComboError } = useGetExportCombo({ errorToast: true })
  // 콤보는 코드/명 배열이 짝을 이룬다. 명(COMBO_OPT_NM)이 비면 코드로 대체한다(ST02 와 같은 규약).
  const options = useMemo(
    () =>
      (combo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: combo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [combo],
  )

  const [reasonCode, setReasonCode] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')

  // 열릴 때마다 초기화 — 컨덕터 `openResetFields: ['reason','detail']`.
  useEffect(() => {
    if (!open) return
    setReasonCode('')
    setReasonDetail('')
  }, [open])

  const handleOk = useCallback(() => {
    if (!reasonCode) return notice.invalid('사유코드를 선택해 주세요.')
    const detail = reasonDetail.trim()
    if (!detail) return notice.invalid('상세사유를 입력해 주세요.')
    if (detail.length < REASON_DETAIL_MIN_LENGTH) return notice.invalid(`상세사유는 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`)
    // Input 의 maxLength 가 1차 방어, 붙여넣기·IME 조합으로 넘친 값은 여기서 막는다.
    if (detail.length > REASON_DETAIL_MAX_LENGTH) return notice.invalid(`상세사유는 ${REASON_DETAIL_MAX_LENGTH}글자 이하로 입력해 주세요.`)
    return onConfirm({ reasonCode, reasonDetail: detail })
  }, [reasonCode, reasonDetail, onConfirm])

  // 제목 '다운로드 사유' — 재핸드오프 산출물 실측(2026-09-08). 그룹 기본값은 '엑셀 다운로드'지만 저장본(DB)이 정본이다.
  return (
    <core.Modal open={open} title="다운로드 사유" width={520} okText="확인" cancelText="취소" onOk={handleOk} onCancel={onCancel}>
      <core.Form layout="horizontal">
        <core.FormItem label="사유코드" required labelWidth={FI_LABEL_WIDTH}>
          {/* 콤보를 받아오는 동안은 빈 라디오 대신 로딩을 알린다 (ST02 준용). */}
          {isComboLoading ? (
            <core.Typography.Text type="secondary" style={{ lineHeight: '32px' }}>
              사유코드를 불러오는 중입니다…
            </core.Typography.Text>
          ) : isComboError ? (
            <core.Typography.Text type="danger" style={{ lineHeight: '32px' }}>
              사유코드를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
            </core.Typography.Text>
          ) : (
            <core.Radio.Group value={reasonCode} onChange={(e) => setReasonCode(String(e.target.value))} options={options} />
          )}
        </core.FormItem>
        <core.FormItem label="상세사유" required labelWidth={FI_LABEL_WIDTH}>
          {/* 상한(200)은 데이터 계약 — 산출물엔 없지만 main 의 검증을 그대로 둔다. */}
          <core.Input value={reasonDetail} placeholder="사유를 입력하세요" maxLength={REASON_DETAIL_MAX_LENGTH} onChange={(e) => setReasonDetail(e.target.value)} style={{ width: '100%' }} />
          <core.Typography.Text style={{ fontSize: 12 }} type="secondary">{`${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`}</core.Typography.Text>
        </core.FormItem>
      </core.Form>
    </core.Modal>
  )
}
